/**
 * Risk inbox (garment Increment 9, M10) — the founder's cross-style "what's on
 * fire" view. Lists every in-flight Style File with a risk (overdue/imminent
 * ex-factory, slipped T&A, a QC-blocked dispatch), highs first, each row a
 * one-click jump to the Style File. Risk reasons are computed deterministically
 * server-side (`garment/order-book`); this screen only renders them.
 *
 * @see prototype/garment/style-file.html — risk pills (the briefing/order-book
 *      board + AI narration + 7 AM WhatsApp send are Increment-9 follow-ons)
 */
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ShieldAlert, TriangleAlert } from "lucide-react";
import { Alert, StatusPill } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { styleStatusLabel, styleStatusTone, type StyleStatus } from "../lib/status.js";

export function RisksInboxPage() {
  const query = trpc.orderBook.listRisks.useQuery({});

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-ink">Risk inbox</h1>
          <p className="mt-0.5 text-sm text-muted">In-flight styles that need attention — highest risk first.</p>
        </div>
        {query.data ? (
          <div className="flex items-center gap-2">
            <StatusPill tone="bad" size="sm" dot>{query.data.highCount} high</StatusPill>
            <StatusPill tone="warn" size="sm" dot>{query.data.mediumCount} watch</StatusPill>
          </div>
        ) : null}
      </header>

      {query.isLoading ? (
        <p className="text-sm text-muted">Loading risks…</p>
      ) : query.error ? (
        <Alert intent="bad">Couldn't load risks. {query.error.message}</Alert>
      ) : !query.data || query.data.risks.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-bg px-4 py-10 text-center">
          <ShieldAlert className="mx-auto mb-2 text-emerald-600" size={22} />
          <p className="text-sm font-medium text-ink-2">No styles at risk</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
            Every in-flight order is on track — no overdue ex-factory dates, slipped milestones, or QC blocks.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {query.data.risks.map((r) => (
            <Link
              key={r.styleId}
              to="/merchandising/styles/$styleId/overview"
              params={{ styleId: r.styleId }}
              className={`block rounded-md border bg-paper p-3 transition-colors hover:border-sky-300 ${r.severity === "high" ? "border-l-[3px] border-l-bad" : "border-l-[3px] border-l-warn"} border-line`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {r.severity === "high" ? (
                  <TriangleAlert size={15} className="text-bad" />
                ) : (
                  <AlertTriangle size={15} className="text-warn" />
                )}
                <span className="font-mono text-sm font-semibold text-ink">{r.styleRef}</span>
                <span className="text-sm text-ink-2">{r.name}</span>
                <StatusPill tone={styleStatusTone(r.status as StyleStatus)} size="sm" dot>
                  {styleStatusLabel(r.status as StyleStatus)}
                </StatusPill>
                <span className="ml-auto text-xs text-muted">
                  {r.buyerName}
                  {r.exFactoryDate ? ` · ex-factory ${formatDate(r.exFactoryDate)}` : ""}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.reasons.map((reason) => (
                  <span
                    key={reason.code}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${reason.severity === "high" ? "bg-bad-50 text-bad" : "bg-warn-50 text-warn"}`}
                  >
                    {reason.label}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
